// client/src/pages/CartPage.js
import React, { useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Container, Box, Typography, Stack, Card, CardContent, IconButton, Button, Divider, Grid, Chip,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { CartContext } from '../context/CartContext';
import { AuthContext } from '../context/AuthContext';
import { FeatureFlagContext } from '../context/FeatureFlagContext';
import { formatCurrency } from '../utils/currency';
import { getImageUrl } from '../utils/media';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import EmptyState from '../components/EmptyState';

const CartPage = () => {
  const { cartItems, removeFromCart, getCartTotal } = useContext(CartContext);
  const { user } = useContext(AuthContext);
  const { isEnabled } = useContext(FeatureFlagContext);
  const navigate = useNavigate();

  const subtotal = getCartTotal();

  const handleProceed = () => {
    // Checkout itself offers a "Continue as Guest" option when that feature
    // is on, so only force a login redirect when guest checkout is off.
    if (!user && !isEnabled('guest_checkout')) {
      navigate('/login?redirect=/checkout');
      return;
    }
    navigate('/checkout');
  };

  if (cartItems.length === 0) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <EmptyState
          icon={<ShoppingCartOutlinedIcon />}
          title="Your Cart is Empty"
          description="Looks like you haven't added anything yet. Let's fix that."
          actionLabel="Continue Shopping"
          actionTo="/"
        />
      </Container>
    );
  }

  const itemCount = cartItems.reduce((count, item) => count + item.quantity, 0);

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 3, md: 6 } }}>
      <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.14em', fontWeight: 700 }}>
        Checkout
      </Typography>
      <Typography variant="h3" fontWeight={800} letterSpacing="-0.02em" sx={{ mb: 4, fontSize: { xs: '2rem', md: '2.6rem' } }}>
        Your cart <Typography component="span" variant="h5" color="text.secondary" fontWeight={600}>({itemCount} {itemCount === 1 ? 'item' : 'items'})</Typography>
      </Typography>

      <Grid container spacing={4} alignItems="flex-start">
        <Grid item xs={12} md={7} lg={8}>
          <Stack spacing={2}>
            {cartItems.map((item) => (
              <Card key={item.cartKey} sx={{ '&:hover': { transform: 'none', boxShadow: 'none' } }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, md: 3 }, p: { xs: 2, md: 2.5 } }}>
                  <Box
                    sx={{
                      width: { xs: 84, md: 112 }, height: { xs: 84, md: 112 }, flexShrink: 0,
                      borderRadius: 3, bgcolor: 'action.hover', display: 'grid', placeItems: 'center', p: 1,
                    }}
                  >
                    <Box component="img" src={getImageUrl(item.imageUrl)} alt={item.name} sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  </Box>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" fontWeight={700} noWrap>{item.name}</Typography>
                    {item.variantLabel && (
                      <Typography variant="body2" color="text.secondary">{item.variantLabel}</Typography>
                    )}
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1 }}>
                      <Chip size="small" label={`Qty ${item.quantity}`} variant="outlined" />
                      <Typography variant="body2" color="text.secondary">{formatCurrency(item.price)} each</Typography>
                    </Stack>
                  </Box>
                  <Stack alignItems="flex-end" spacing={0.5}>
                    <Typography variant="h6" fontWeight={800}>{formatCurrency(item.price * item.quantity)}</Typography>
                    <IconButton color="error" size="small" onClick={() => removeFromCart(item.cartKey)} aria-label="Remove item">
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </Grid>

        <Grid item xs={12} md={5} lg={4} sx={{ position: { md: 'sticky' }, top: { md: 96 } }}>
          <Card sx={{ p: { xs: 2.5, md: 3 } }}>
            <Typography variant="h6" fontWeight={800} gutterBottom>Order summary</Typography>
            <Stack spacing={1.25} sx={{ mt: 2 }}>
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">Subtotal</Typography>
                <Typography fontWeight={600}>{formatCurrency(subtotal)}</Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Prices include VAT. Shipping and any discounts are calculated at checkout.
              </Typography>
            </Stack>
            <Divider sx={{ my: 2.5 }} />
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 2.5 }}>
              <Typography variant="subtitle1" fontWeight={700}>Estimated total</Typography>
              <Typography variant="h5" fontWeight={800}>{formatCurrency(subtotal)}</Typography>
            </Stack>
            <Button fullWidth variant="contained" size="large" onClick={handleProceed} sx={{ py: 1.5, fontWeight: 700 }}>
              Checkout securely
            </Button>
            <Button fullWidth size="small" component={Link} to="/" sx={{ mt: 1 }}>
              Continue shopping
            </Button>
            <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="center" sx={{ mt: 2.5, color: 'text.secondary' }}>
              <LockOutlinedIcon sx={{ fontSize: 14 }} />
              <Typography variant="caption">256-bit SSL encrypted checkout</Typography>
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default CartPage;
